import * as React from 'react';
import { PureComponent } from 'react';
import DG from '2gis-maps';
import 'leaflet.markercluster';
import get from 'lodash.get';
import isEqual from 'lodash.isequal';
import noop from 'lodash.noop';
import cn from 'classnames';
import { connect } from 'react-redux';

import DataPointMap, { Point } from './inner/DataPointMap';
import { GeoJson } from './inner/DataGeoJson';
import { iconGenerator, ICON_CLASS } from './inner/iconGenerator';

import { legendVisibilitySelector } from '@selectors/uiSelector';

import {
  filteredOfficesCountSelector,
  officesCountSelector,
} from '@selectors/filterServicesSelector';

import './styles/GisMap.scss';

const tilesHost = window.location.hostname;
const TILE_CONFIG = {
  tileServer: `//${tilesHost}/api/map/v1/geo/tiles?x={x}&y={y}&z={z}&ts=online_sd&layerType=nc`,
  retinaTileServer: `//${tilesHost}/api/map/v1/geo/tiles?x={x}&y={y}&z={z}&ts=online_hd&layerType=nc`,
  // Сейчас API игнорирует параметр size, поэтому используем тот же сервер, что и для обычных тайлов
  previewTileServer: `//${tilesHost}/api/map/v1/geo/tiles?x={x}&y={y}&z={z}&ts=online_sd&layerType=nc`,
  previewRetinaTileServer: `//${tilesHost}/api/map/v1/geo/tiles?x={x}&y={y}&z={z}&ts=online_hd&layerType=nc`,
};


DG.config = {
  ...DG.config,
  ...TILE_CONFIG,
};

// Around Moscow
const MAX_NORTH_EAST = DG.latLng(56.788845, 39.91333);
const MAX_SOUTH_WEST = DG.latLng(50.565692, 33.628662);

const DEFAULTS = {
  CITY_ZOOM: 10,
  OBJECT_ZOOM: 17,
  KREMLIN_LATLNG: [55.751288, 37.617928],
  CONDENSED_MAP_MIN_MARKERS: 100,
  CONDENSED_MAP_MAX_ZOOM: 12,
  DRAW_POINTS_DELAY: 300,

  MAP_OPTIONS: {
    preferCanvas: true,
    fullscreenControl: false,
    locationControl: true,
    poi: false,
    minZoom: 9,
    maxBounds: DG.latLngBounds(MAX_NORTH_EAST, MAX_SOUTH_WEST),
    animate: true,
  },

  BOUNDS_OPTIONS: {
    padding: [10, 10],
  },

  CLUSTER_OPTIONS: {
    chunkedLoading: true,
    chunkInterval: 50,
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: false,
    disableClusteringAtZoom: 17,
    spiderLegPolylineOptions: {
      opacity: 0,
    },
    iconCreateFunction: () => iconGenerator.generateCluster(),
  },
};

/**
 * @see https://github.com/Microsoft/TypeScript/issues/11305#issuecomment-252576980
 * > ...you should use the global MutationObserver variable instead of window.MutationObserver because that one has declarations in TypeScript.
 */
// @ts-ignore
const MObserver = MutationObserver || window.WebKitMutationObserver;

const routeStyle = (routeStyleHide) => {
  let opacity = 1;
  if (routeStyleHide) {
    opacity = 0;
  }
  return {
    style: {
      color: '#0848C0',
      weight: 5,
      dashArray: '8',
      opacity,
    },
  };
};

interface Props {
  /**
   * Массив точек, которые надо отобразить на карте. Каждая точка <b>должна</b> быть объектом,
   * содержащим в себе одну из пар полей:<br />
   * <ul>
   *   <li><code>{lat, lng}</code>.</li>
   *   <li><code>{latitude, longitude}</code>.</li>
   * </ul>
   * Если хотя-бы одной из указанных пар нет, то точка просто не будет отображаться на карте без
   * каких-либо предупреждений.
   */
  points?: Point[];

  /**
   * Выбранные точки. Ограничение такое-же, как и на <code>points</code>. Свойство принимает
   * массив, но подсвечивает маркер <b>всегда</b> только первой точки.<br />
   * <br />
   * Сделано это прежде всего для того, чтобы, была консистентность с методом
   * <code>onPointClick</code>, в котором передается массив точек (согласно требований у нас в
   * одной точке может находиться несколько объектов, и это требование компонент карты
   * поддерживает.
   */
  selectedPoints?: Point[];

  /**
   * Указывает, реагировать ли маркерам на клик мышкой.
   */
  markersActive?: boolean;

  /**
   * Зум, к которому будет приводиться карты после отрисовки всех точек. Если установлен в
   * <code>false</code>, то зуммирование будет происходить в зависимости от количества точек:
   * <ul>
   *   <li>если нет точек: <code>DEFAULTS.CITY_ZOOM</code>.</li>
   *   <li>если одна точка: <code>DEFAULTS.OBJECT_ZOOM</code>.</li>
   *   <li>если несколько точек: так, чтобы вместить на карте максимальное кол-во точек, но не
   *   выйти за пределы допустимой области просмотра (устанавливается свойством
   *   <code>maxBounds</code>).</li>
   * </ul>
   */
  defaultZoom?: number;

  /**
   * Точка, по которой будет центрироваться карта после отрисовки всех точек. Если установлена в
   * <code>false</code>, то центровка будет происходить в зависимости от количества точек:
   * <ul>
   *   <li>если нет точек: с видом на Кремль.</li>
   *   <li>если одна точка: чтобы точка была по-центру карты.</li>
   *   <li>если несколько точек: так, чтобы вместить на карте максимальное кол-во точек, но не
   *   выйти за пределы допустимой области просмотра (устанавливается свойством maxBounds).</li>
   * </ul>
   */
  defaultCenter?: {
    lat: number;
    lng: number;
  };

  /**
   * Опции, которые можно подсунуть при вызове fitBounds метода карты
   *
   * Например, paddingTopLeft - задает отступ от верхнего левого угла контейнера карты,
   * который не должен учитываться при подстройке центра и масштаба.
   * Удобно использовать, например, если на карте приложения имеется левая панель и вы не хотите,
   * чтобы при масштабировании под ней скрывались объекты.
   *
   * Подробнее по опциям тут:
   * http://api.2gis.ru/doc/maps/en/manual/map#fitbounds-options
   *
   * или по-русски:
   * http://api.2gis.ru/doc/maps/ru/manual/map#%D0%BE%D0%BF%D1%86%D0%B8%D0%B8-%D1%81%D0%BE%D0%BE%D1%82%D0%B2%D0%B5%D1%82%D1%81%D1%82%D0%B2%D0%B8%D1%8F%20%D0%B3%D1%80%D0%B0%D0%BD%D0%B8%D1%86%D0%B0%D0%BC
   */
  boundsOptions: {
    paddingTopLeft?: number[];
    paddingBottomRight?: number[];
    padding?: number[];
    maxZoom?: number;
  };

  /**
   * Опции, которые может принимать карта.
   * С помощью этого можно расширить DEFAULTS.MAP_OPTIONS
   *
   * Например, передать scrollWheelZoom - флаг отвечающий за зуминг карты с помощью колесика мышки
   *
   * В PropTypes.shape разместил минимальный набор опций,
   * которые, как мне кажется может понадобится изменять.
   * Можно добавлять и другие из документации, просто PropTypes будет ругаться.
   *
   * Подробная документация по опциям тут:
   * http://api.2gis.ru/doc/maps/ru/manual/map/#%D0%BE%D0%BF%D1%86%D0%B8%D0%B8
   *
   */
  mapOptions: {
    scrollWheelZoom?: boolean | 'center'; // true/false/'center'
    touchZoom?: boolean | 'center'; // true/false/'center'
    zoomControl?: boolean;

    fullscreenControl?: boolean;
    locationControl?: boolean;
    poi?: boolean;
    minZoom?: number;
    animate?: boolean;
  };
  /**
   * Инициировать карту общим видом Москвы.<br />
   * Если точек много (или если идут дополнительные запросы к API), то между показом карты и
   * отрисовкой точек проходит какое-то время. Чтобы у пользователя не было ощущения подвисания,
   * в это время можно порадовать его общим видом столицы, установив это свойство в
   * <code>true</code>.
   */
  moscowViewOnInit?: boolean;

  /**
   * Включает/отключает центровку маркера при выборе точки, независимо от того, выбирается точка
   * кликом, или через свойство <code>selectedPoints</code>.
   */
  centerIfSelected?: boolean;

  /**
   * Включает/отключает зуммирование при выборе точки, независимо от того, выбирается точка
   * кликом, или через свойство selectedPoints.
   */
  zoomIfSelected?: boolean;

  /**
   * Включает/отключает отображение количества точек на маркерах, где в одной паре координат
   * присутствует несколько объектов.
   */
  countsVisible?: boolean;

  /**
   * Включает/отключает режим "сжатых точек". Этот режим описан в документе:<br />
   * https://projects.invisionapp.com/d/main#/console/12765622/269445416/preview<br />
   * и заключается в том, что когда точек слишком много, а также у нас определенный зум, то
   * маркеры отображаются в уменьшенном виде (чтобы карта не выглядела как лицо ребенка болеющего
   * ветрянкой).<br />
   * Подробные условия можно найти в вышеуказанном документе.<br />
   * Это свойство по-умолчанию установлено в <code>false</code>, так как при использовании плагина
   * <b>leaflet.markercluster</b> такая ситуация практически никогда не возникает (точки активно
   * объединяются в кластеры).<br />
   * Свойство может быть актуальным при большом количестве точек и отключенной кластеризации.
   */
  condensedMode?: boolean;

  /**
   * Включает/отключает кластеризацию точек.
   */
  clustering?: boolean;

  /**
   * Дополнительные css-классы для контейнера карты.
   */
  className?: string;

  /**
   * Функция, которая будет вызвана при клике на маркер.<br />
   * Передаваемые параметры:
   * <ol>
   *   <li>массив объектов, соответствующих маркеру.</li>
   * </ol>
   */
  onPointClick(points: []): void | undefined;

  /**
   * Функция, которая вызывается при инициализации карты (в момент первой центровки или первой
   * установки зума).<br />
   * Передаваемые параметры:
   * <ol>
   *   <li>объект карты.</li>
   * </ol>
   */
  onMapLoad(map: any): void | undefined;

  /**
   * Функция, которая вызывается при зуммировании карты. Может быть полезна для сохранения
   * текущего зума и использования его при перерисовке карты.<br />
   * Передаваемые параметры:
   * <ol>
   *   <li>новый зум.</li>
   * </ol>
   */
  onZoom(center: any, zoom: number): void | undefined;

  /**
   * Функция, которая вызывается при передвижении карты. Может быть полезна для сохранения
   * текущего центра и зума и их использования при перерисовке карты.<br />
   * Передаваемые параметры:
   * <ol>
   *   <li>новый центр.</li>
   *   <li>новый зум.</li>
   * </ol>
   */
  onMove(center: any, zoom: number): void | undefined;

  routeGeoJSON?: GeoJson[];

  routeStyleHide?: boolean;

  visibilityLegend?: boolean;

  filteredOfficesCount: number;

  officesCount: number;

  isPhone?: boolean;

  routeSelected?: {};
}

/**
 * Компонент, инкапсулирующий работу с картой 2gis.<br />
 * <br />
 * Дизайн и поведение соответствуют документу:<br />
 * https://projects.invisionapp.com/d/main#/console/12765622/269445416/preview<br />
 * <br />
 * По-умолчанию размер карты установлен в 600х600 пикселей.
 */
class GisMap extends PureComponent<Props> {
  mapRef = React.createRef<HTMLDivElement>();

  static defaultProps = {
    points: [],
    selectedPoints: [],
    markersActive: true,
    defaultZoom: null,
    defaultCenter: null,
    boundsOptions: {},
    mapOptions: {},
    moscowViewOnInit: false,
    centerIfSelected: true,
    zoomIfSelected: true,
    countsVisible: true,
    condensedMode: false,
    clustering: true,
    className: '',
    onPointClick: noop,
    onMapLoad: noop,
    onZoom: noop,
    onMove: noop,
    routeStyleHide: false,
  };

  state = {
    mapInitialized: false,
    map: null,
    group: null,
    markerByHashMap: {},
    selectedMarkers: [],
    zoom: DEFAULTS.CITY_ZOOM,
    center: DEFAULTS.KREMLIN_LATLNG,
    visibleMarkersCount: null,
    drawPointsTimeoutId: null,
    determineVisibleMarkersObserver: null,
  };

  componentDidMount() {
    const { points, moscowViewOnInit } = this.props;

    if (moscowViewOnInit) {
      this.drawMoscow();
    }

    if (points.length > 0) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState({
        drawPointsTimeoutId: setTimeout(this.draw, DEFAULTS.DRAW_POINTS_DELAY),
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { points, selectedPoints, routeStyleHide, routeGeoJSON } = this.props;
    if (
      !isEqual(points, prevProps.points) ||
      routeStyleHide !== prevProps.routeStyleHide ||
      prevProps.routeGeoJSON !== routeGeoJSON
    ) {
      this.draw();
    }

    if (!isEqual(selectedPoints, prevProps.selectedPoints)) {
      const { map, group, markerByHashMap } = this.state;
      let { selectedMarkers } = this.state;

      selectedMarkers = this.selectMarker(
        map,
        group,
        markerByHashMap,
        selectedMarkers
      );
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ selectedMarkers });
    }
  }

  componentWillUnmount() {
    const { drawPointsTimeoutId, determineVisibleMarkersObserver } = this.state;

    clearTimeout(drawPointsTimeoutId);

    if (determineVisibleMarkersObserver) {
      determineVisibleMarkersObserver.disconnect();
    }
  }

  modifyMapControls = () => {
    const { current: mapContainer } = this.mapRef;
    const locationControl = mapContainer.querySelector(
      '.leaflet-bottom.leaflet-right .dg-control-round.leaflet-control'
    );

    if (locationControl) {
      const leftTopPan = mapContainer.querySelector(
        '.leaflet-top.leaflet-left'
      );
      leftTopPan.append(locationControl);
    }
  };

  initializeVisibleMarkersObserver = () => {
    const { current: mapContainer } = this.mapRef;
    let { determineVisibleMarkersObserver } = this.state;

    if (determineVisibleMarkersObserver) {
      determineVisibleMarkersObserver.disconnect();
    }

    determineVisibleMarkersObserver = new MObserver(() => {
      const visibleMarkers = mapContainer.querySelectorAll(
        `.${ICON_CLASS.MARKER_NATIVE}`
      );

      this.setState({
        visibleMarkersCount: visibleMarkers ? visibleMarkers.length : 0,
      });
    }).observe(mapContainer.querySelector('.leaflet-marker-pane'), {
      childList: true,
    });

    this.setState({ determineVisibleMarkersObserver });
  };

  updateMarkerGroups = (zoom: number) => {
    const { markersActive, selectedPoints, countsVisible } = this.props;
    const { zoom: prevZoom } = this.state;
    // @ts-ignore Костыль для проброса маркеров во время реинициализации и зума, нужно сделать fitBounds после инициализации
    const { markerByHashMap } = window;

    if ((prevZoom < 16 && zoom >= 16) || (prevZoom > 15 && zoom <= 15)) {
      Object.values(markerByHashMap).forEach((marker: any) => {
        const { dataPoint } = marker.options;
        if (dataPoint.pointsCount > 1) {
          Object.values(dataPoint.dataMap).forEach((mark: any, index) => {
            const hash = DataPointMap.generateLatLngHash(mark);
            const selected = selectedPoints.some(
              (item) => mark.hasPoint && mark.hasPoint(item)
            );
            let pointsCount = 0;
            let className = '';

            if (zoom >= 16) {
              const icon = iconGenerator.generatePlace(
                0,
                selected,
                markersActive,
                countsVisible,
                dataPoint.className
              );
              markerByHashMap[hash].setIcon(icon);

              if (index !== 0) {
                markerByHashMap[hash].setOpacity(1);
              }
            } else if (zoom <= 15) {
              if (index === 0) {
                // eslint-disable-next-line prefer-destructuring
                pointsCount = dataPoint.pointsCount;
                // eslint-disable-next-line prefer-destructuring
                className = dataPoint.className;
              } else if (!selected) {
                pointsCount = 0;
                className = `${dataPoint.className} GisMap__marker--opacity`;
                markerByHashMap[hash].setOpacity(0);
              }

              const icon = iconGenerator.generatePlace(
                pointsCount,
                selected,
                markersActive,
                countsVisible,
                className
              );
              markerByHashMap[hash].setIcon(icon);
            }
          });
        }
      });
    }
  };

  handleMapLoad = (event) => {
    const { condensedMode, onMapLoad } = this.props;

    if (condensedMode) {
      this.initializeVisibleMarkersObserver();
    }

    this.modifyMapControls();
    // eslint-disable-next-line no-underscore-dangle
    this.updateMarkerGroups(event.target._zoom);
    onMapLoad(event.target);
  };

  handleZoomChange = (event) => {
    const { onZoom } = this.props;
    const zoom = event.target.getZoom();
    const center = event.target.getCenter();
    this.updateMarkerGroups(zoom);
    this.setState({ zoom }, () => onZoom(center, zoom));
  };

  handleMapMove = (event) => {
    const { onMove } = this.props;
    const zoom = event.target.getZoom();
    const center = event.target.getCenter();

    this.setState({ zoom, center }, () => onMove(center, zoom));
  };

  generateMarkers = (dataPoints) => {
    const { markersActive, selectedPoints, countsVisible } = this.props;
    const { zoom } = this.state;

    const markers = [];

    Object.values(dataPoints.pointsByHash).forEach((dataPoint: any) => {
      Object.values(dataPoint.dataMap).forEach((data: any, index) => {
        const farZoom = zoom <= 15;
        const markerDataPoint = {
          ...data,
          dataMap: index === 0 ? dataPoint.dataMap : data.dataMap,
          pointsCount: index === 0 ? dataPoint.pointsCount : 1,
        };
        const pointsCount = farZoom && index === 0 ? dataPoint.pointsCount : 1;
        const { lat, lng, className, opacity } = markerDataPoint;
        const selected = selectedPoints.some((item) =>
          markerDataPoint.hasPoint(item)
        );
        const icon = iconGenerator.generatePlace(
          pointsCount,
          selected,
          markersActive,
          countsVisible,
          `${className}${
            opacity === 0 && farZoom ? ' GisMap__marker--opacity' : ''
          }`
        );

        markers.push(
          DG.marker([lat, lng], {
            icon,
            dataPoint: markerDataPoint,
            opacity: farZoom ? markerDataPoint.opacity : 1,
          })
        );
      });
    });

    return markers;
  };

  generateGroup = (markers, map) => {
    const { clustering, onPointClick, markersActive } = this.props;
    const group = clustering
      ? DG.markerClusterGroup(DEFAULTS.CLUSTER_OPTIONS)
      : DG.featureGroup();

    if (markersActive) {
      group.on('click', (event) => {
        const dataPoint = get(event, 'layer.options.dataPoint', null);
        const points = dataPoint.getPoints() || [];
        if (points.length)
          map.setView([points[0].latitude, points[0].longitude], 17);
        onPointClick(points);
      });
    }
    markers.forEach((marker) => marker.addTo(group));
    return group;
  };

  drawMoscow = () => {
    const { mapOptions } = this.props;
    const { current: container } = this.mapRef;

    if (!container) {
      return;
    }

    const map = DG.map(container, {
      ...DEFAULTS.MAP_OPTIONS,
      ...mapOptions,
    });

    this.modifyMapControls();
    this.initCityView(map);
    this.setState({ map });
  };

  draw = () => {
    let { map, group, markerByHashMap } = this.state;
    const { points, mapOptions, routeGeoJSON, routeStyleHide } = this.props;
    const { current: container } = this.mapRef;
    const dataPoints = new DataPointMap(points);
    const markers = this.generateMarkers(dataPoints);

    if (!container) {
      return;
    }

    if (map) {
      map.remove();
    }

    map = DG.map(container, {
      ...DEFAULTS.MAP_OPTIONS,
      ...mapOptions,
    });

    markerByHashMap = {};
    markers.forEach((marker) => {
      const hash = DataPointMap.generateLatLngHash(marker.getLatLng());
      markerByHashMap[hash] = marker;
    });
    // @ts-ignore
    window.markerByHashMap = markerByHashMap;

    map.on('load', this.handleMapLoad);
    map.on('zoom', this.handleZoomChange);
    map.on('move', this.handleMapMove);

    group = this.generateGroup(markers, map);

    const cityView = markers.length === 0 || !group.getBounds().isValid();
    const onePointView = markers.length === 1;
    const multiplePointsView =
      markers.length > 1 && group.getBounds().isValid();

    if (cityView) {
      this.initCityView(map);
    }

    if (onePointView) {
      group.addTo(map);
      this.initOnePointView(map, dataPoints);
    }

    if (multiplePointsView && !routeGeoJSON) {
      group.addTo(map);
      this.initMultiplePointsView(map, { group, route: null });
    }

    if (multiplePointsView && routeGeoJSON) {
      const route = DG.geoJSON(routeGeoJSON, routeStyle(routeStyleHide));
      route.addTo(map);
      this.initMultiplePointsView(map, { route, group: null });
      group.addTo(map);
    }

    const mapInitialized = true;
    const selectedMarkers = this.selectMarker(map, group, markerByHashMap, []);

    this.setState({
      mapInitialized,
      map,
      group,
      markerByHashMap,
      selectedMarkers,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  initCityView = (map) => {
    const center = DEFAULTS.KREMLIN_LATLNG;
    const zoom = DEFAULTS.CITY_ZOOM;

    map.setView(center, zoom);
  };

  initOnePointView = (map, dataPoints) => {
    const { defaultZoom } = this.props;
    const center = dataPoints.get(0).getLatLng();
    const zoom = defaultZoom || DEFAULTS.OBJECT_ZOOM;

    map.setView(center, zoom);
  };

  initMultiplePointsView = (map, { group, route }) => {
    const { mapInitialized } = this.state;
    const {
      defaultZoom,
      defaultCenter,
      boundsOptions: propsBoundsOptions,
      zoomIfSelected,
      centerIfSelected,
      isPhone,
    } = this.props;

    const shouldFitBounds = mapInitialized && !(defaultCenter || defaultZoom);
    const shouldCenterZoom = !shouldFitBounds && defaultCenter && defaultZoom;
    const shouldCenterOnly = !shouldCenterZoom && defaultCenter;
    const shouldZoomOnly = !shouldCenterZoom && defaultZoom;
    const isDetailed = zoomIfSelected && centerIfSelected;

    const boundsOptions = { ...DEFAULTS.BOUNDS_OPTIONS, ...propsBoundsOptions };

    if (shouldFitBounds) {
      const bounds = route ? route.getBounds() : group.getBounds();
      // const center = bounds.getCenter();
      // const zoom = defaultZoom || DEFAULTS.OBJECT_ZOOM;
      // map.setView(center, zoom);
      let options = {
        ...boundsOptions,
        paddingTopLeft: [400, 100],
        paddingBottomRight: [50, 100],
      };

      if (isDetailed) {
        options = {
          ...boundsOptions,
          paddingTopLeft: [20, 100],
          paddingBottomRight: [20, 100],
        };
      }

      if (isPhone) {
        options = {
          ...boundsOptions,
          paddingTopLeft: [30, 30],
          paddingBottomRight: [30, 30],
        };
      }

      map.fitBounds(bounds, options);
    }

    if (route) {
      const bounds = route.getBounds();
      let options = {
        ...boundsOptions,
        paddingTopLeft: [400, 100],
        paddingBottomRight: [50, 100],
      };

      if (isDetailed) {
        options = {
          ...boundsOptions,
          paddingTopLeft: [20, 100],
          paddingBottomRight: [20, 100],
        };
      }

      if (isPhone) {
        options = {
          ...boundsOptions,
          paddingTopLeft: [30, 30],
          paddingBottomRight: [30, 30],
        };
      }

      map.fitBounds(bounds, options);
    }

    if (shouldCenterOnly) {
      map.setView(defaultCenter);
    }

    if (shouldCenterZoom || (shouldZoomOnly && group)) {
      const { visibilityLegend, isPhone } = this.props;
      const options = () => {
        if (!isPhone && !visibilityLegend) {
          return { paddingTopLeft: [350, 20], paddingBottomRight: [20, 20] };
        }
        return boundsOptions;
      };
      setTimeout(() => map.fitBounds(group.getBounds(), options()), 0);
    }
  };

  selectMarker = (map, group, markerByHashMap, selectedMarkers) => {
    const { zoom, center } = this.state;
    const {
      clustering,
      selectedPoints,
      zoomIfSelected,
      centerIfSelected,
      markersActive,
      countsVisible,
      defaultZoom: propsDefaultZoom,
      routeGeoJSON,
      filteredOfficesCount,
      officesCount,
    } = this.props;

    const defaultZoom = propsDefaultZoom || DEFAULTS.OBJECT_ZOOM;
    const setCenter = () => {
      if (routeGeoJSON) {
        return;
      }

      const {
        0: { lat, lng, latitude, longitude },
      } = selectedPoints;

      map.setView([lat || latitude, lng || longitude], map.getZoom());
    };
    const setZoom = () => {
      const zoomItem = Math.max(map.getZoom(), defaultZoom);
      map.setZoom(zoomItem);
    };

    const setCenterAndZoom = (zoom = 0) => {
      if (routeGeoJSON) {
        return;
      }

      const {
        0: { lat, lng, latitude, longitude },
      } = selectedPoints;

      const zoomItem = zoom || Math.max(map.getZoom(), defaultZoom);
      map.setView([lat || latitude, lng || longitude], zoomItem);
    };

    const keepCenterAndZoom = () => {
      if (routeGeoJSON) {
        return;
      }
      map.setView(center, zoom);
    };

    let selectedMarker = null;
    const farZoom = zoom <= 15;

    // Deselect all previously selected markers
    while (selectedMarkers.length > 0) {
      const marker = selectedMarkers.pop();
      const { dataPoint } = marker.options;
      const selected = false;
      const { opacity } = dataPoint;

      const icon = iconGenerator.generatePlace(
        farZoom ? dataPoint.pointsCount : 0,
        selected,
        markersActive,
        countsVisible,
        `${dataPoint.className}${
          opacity === 0 && farZoom ? ' GisMap__marker--opacity' : ''
        }`
      );
      if (farZoom && opacity === 0) {
        marker.setOpacity(0);
      }
      marker.setIcon(icon);
    }

    if (selectedPoints.length > 0) {
      const hash = DataPointMap.generateLatLngHash(selectedPoints[0]);
      selectedMarker = markerByHashMap[hash];
    }

    // Select marker from first point from props
    if (selectedMarker) {
      const { dataPoint } = selectedMarker.options;
      const selected = true;
      const icon = iconGenerator.generatePlace(
        farZoom ? dataPoint.pointsCount : 0,
        selected,
        markersActive,
        countsVisible
      );
      selectedMarker.setIcon(icon);
      selectedMarkers.push(selectedMarker);
    }

    // Center and zoom
    if (selectedMarker) {
      if (
        clustering &&
        group.getVisibleParent(selectedMarker) !== selectedMarker
      ) {
        // Zoom if selected marker inside cluster
        group.zoomToShowLayer(selectedMarker, () => {
          if (centerIfSelected && zoomIfSelected) {
            setCenterAndZoom();
          }
        });
      } else if (centerIfSelected && zoomIfSelected) {
        setCenterAndZoom(16);
      } else if (centerIfSelected) {
        setCenter();
      } else if (zoomIfSelected) {
        setZoom();
      } else if (!routeGeoJSON) {
        keepCenterAndZoom();
      }
    } else if (!filteredOfficesCount || filteredOfficesCount === officesCount) {
      map.setView(DEFAULTS.KREMLIN_LATLNG, 10);
    }
    return selectedMarkers;
  };

  render() {
    const { condensedMode, className } = this.props;
    const { zoom, visibleMarkersCount } = this.state;
    const mapCssClasses = cn('GisMap', className, {
      'GisMap-condensed':
        condensedMode &&
        zoom < DEFAULTS.CONDENSED_MAP_MAX_ZOOM &&
        visibleMarkersCount >= DEFAULTS.CONDENSED_MAP_MIN_MARKERS,
    });

    return <div className={mapCssClasses} ref={this.mapRef} />;
  }
}

export default connect(
  (state) => ({
    visibilityLegend: legendVisibilitySelector(state),
    // @ts-ignore
    filteredOfficesCount: filteredOfficesCountSelector(state),
    officesCount: officesCountSelector(state),
  }),
  null
)(GisMap);
